'use client';

import { type Ref, type RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { animate, press, useReducedMotion } from 'motion/react';

import { pressScale, pressTransition } from '@shaojiang/lib/motion';

/**
 * Press feedback for any element: shrinks it slightly while held (pointer or Enter key)
 * and springs back on release. Attached imperatively with Motion's `press` gesture so it
 * works with `asChild` and doesn't fight Panda style props such as `transition`.
 *
 * Returns a ref callback that also forwards to `forwardedRef`.
 */
export function usePressAnimation<T extends Element>(
  forwardedRef: Ref<T> | undefined,
  disabled = false,
): RefCallback<T> {
  const [element, setElement] = useState<T | null>(null);
  const forwardedRefRef = useRef(forwardedRef);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    forwardedRefRef.current = forwardedRef;
  });

  useEffect(() => {
    if (!element || disabled || reduceMotion) return;
    return press(element, (target) => {
      animate(target, { scale: pressScale }, pressTransition);
      return () => {
        animate(target, { scale: 1 }, pressTransition);
      };
    });
  }, [element, disabled, reduceMotion]);

  return useCallback((node: T | null) => {
    setElement(node);
    const ref = forwardedRefRef.current;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, []);
}
